// src/components/HistorialVisitaModal.js
import React, { useState, useEffect } from 'react';
import { IoClose, IoEyeOutline, IoTrashOutline } from 'react-icons/io5';
import { db } from '../firebaseConfig';
import { collection, query, where, getDocs, orderBy, limit, deleteDoc, doc } from 'firebase/firestore';
import moment from 'moment';
import 'moment/locale/es';
import { useAuth } from '../context/AuthContext';
import AccesoModal from './AccesoModal'; // --- Import AccesoModal ---
import './HistorialVisitaModal.css';

moment.locale('es');

// Helper to determine status based on access data
const getStatusLabel = (access) => {
    // 1. Prioridad: Lo que diga la BD dictamina la verdad absoluta final
    if (access.estatus === 'Completado' || access.estatus === 'Usado' || access.usosActuales > 0) {
        return { label: 'Usado', className: 'used' };
    }

    if (access.estatus === 'Expirado' || access.estatus === 'Revocado' || access.estatus === 'Cancelado') {
        return { label: 'Expirado', className: 'expired' };
    }

    // 2. Si todavía está 'Activo' en BD, checamos contra el reloj internamente
    const now = new Date();
    const expiry = access.expira?.toDate();
    if (expiry && expiry < now) return { label: 'Expirado', className: 'expired' };

    // Si aún está Activo y en tiempo
    return { label: 'Generado', className: '' };
};

const HistorialVisitaModal = ({ visita, onClose }) => {
    const [accesos, setAccesos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAcceso, setSelectedAcceso] = useState(null); // --- For View Modal ---
    const [confirmDeleteAccesoId, setConfirmDeleteAccesoId] = useState(null); // --- New State for Custom Confirm Delete ---
    const { currentUser } = useAuth();

    const fetchHistory = async () => {
        if (!visita?.id || !currentUser) return;
        setLoading(true);
        try {
            const q = query(
                collection(db, 'accesos'),
                where('visitaRecurrenteId', '==', visita.id),
                where('residenteId', '==', currentUser.uid),
                orderBy('expira', 'desc'),
                limit(20)
            );

            const snap = await getDocs(q);
            const list = [];
            snap.forEach(doc => list.push({ id: doc.id, ...doc.data() }));
            setAccesos(list);
        } catch (error) {
            console.error("Error fetching access history:", error);
            if (error.code === 'failed-precondition') {
                alert("Nota para desarrollador: Se requiere un índice compuesto en Firestore para esta consulta.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visita, currentUser]);

    const handleDelete = (accesoId) => {
        setConfirmDeleteAccesoId(accesoId);
    };

    const handleConfirmDelete = async () => {
        if (!confirmDeleteAccesoId) return;
        const accesoId = confirmDeleteAccesoId;
        setConfirmDeleteAccesoId(null);

        try {
            await deleteDoc(doc(db, 'accesos', accesoId));
            // Update UI locally
            setAccesos(prev => prev.filter(a => a.id !== accesoId));
        } catch (error) {
            console.error("Error deleting access:", error);
            alert("Error al eliminar el acceso.");
        }
    };

    return (
        <>
            <div className="historial-modal-overlay" onClick={onClose}>
                <div className="historial-modal-content" onClick={e => e.stopPropagation()}>
                    <div className="historial-header">
                        <h2>Historial: {visita.nombreCompleto}</h2>
                        <button className="close-btn" onClick={onClose}><IoClose /></button>
                    </div>

                    <div className="historial-body">
                        {loading && <p className="loading-text">Cargando historial...</p>}

                        {!loading && accesos.length === 0 && (
                            <p className="empty-text">No hay registros de accesos generados para esta visita.</p>
                        )}

                        {!loading && accesos.length > 0 && (
                            <div className="historial-list">
                                {accesos.map(acceso => {
                                    const createdDate = acceso.expira ? moment(acceso.expira.toDate()).subtract(6, 'hours') : moment();
                                    const status = getStatusLabel(acceso);

                                    return (
                                        <div key={acceso.id} className="historial-item">
                                            <div className="historial-info-group">
                                                <div className="historial-date-group">
                                                    <span className="historial-date">
                                                        {createdDate.format('D [de] MMMM')}
                                                    </span>
                                                    <span className="historial-time">
                                                        {createdDate.format('h:mm a')}
                                                    </span>
                                                </div>
                                                <span className={`historial-status ${status.className}`}>
                                                    {status.label}
                                                </span>
                                            </div>

                                            <div className="historial-actions">
                                                <button
                                                    className="historial-action-btn view"
                                                    title="Ver Código QR"
                                                    onClick={() => setSelectedAcceso(acceso)}
                                                >
                                                    <IoEyeOutline />
                                                </button>
                                                {status.label === 'Generado' && (
                                                    <button
                                                        className="historial-action-btn delete"
                                                        title="Eliminar Registro"
                                                        onClick={() => handleDelete(acceso.id)}
                                                    >
                                                        <IoTrashOutline />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* --- Render Detail Modal on top --- */}
            {selectedAcceso && (
                <AccesoModal
                    acceso={selectedAcceso}
                    onClose={() => setSelectedAcceso(null)}
                />
            )}

            {/* --- Custom Confirmation Modal --- */}
            {confirmDeleteAccesoId && (
                <div className="hist-modal-overlay">
                    <div className="hist-confirm-modal anim-pop">
                        <div className="hist-confirm-icon-wrapper">
                            <IoTrashOutline size={30} />
                        </div>
                        <h3>Eliminar Acceso</h3>
                        <p>
                            ¿Estás seguro de eliminar este acceso del historial? Esta acción revocará la invitación y no se puede deshacer.
                        </p>
                        <div className="hist-confirm-actions">
                            <button className="h-btn cancel" onClick={() => setConfirmDeleteAccesoId(null)}>
                                Cancelar
                            </button>
                            <button className="h-btn accept" onClick={handleConfirmDelete}>
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default HistorialVisitaModal;
